<template>
  <Teleport to="body">
    <Transition name="panel-fade">
      <div v-if="isPanelOpen" class="notification-panel-overlay" @click.self="closePanel">
        <Transition name="panel-slide">
          <div v-if="isPanelOpen" class="notification-panel">
            <!-- Header -->
            <div class="notification-panel__header">
              <h3 class="notification-panel__title">
                {{ $t('notifications.title') }}
                <span v-if="unreadCount > 0" class="notification-panel__badge">
                  {{ unreadCount }}
                </span>
              </h3>
              <div class="notification-panel__actions">
                <button
                  v-if="unreadCount > 0"
                  class="notification-panel__action"
                  @click="handleMarkAllRead"
                >
                  {{ $t('notifications.markAllRead') }}
                </button>
                <button class="notification-panel__close" @click="closePanel">
                  <XIcon :size="20" />
                </button>
              </div>
            </div>

            <!-- List -->
            <div class="notification-panel__list" @scroll="handleScroll">
              <template v-if="isLoading && notifications.length === 0">
                <div class="notification-panel__loading">
                  <div class="notification-panel__spinner" />
                </div>
              </template>

              <template v-else-if="notifications.length === 0">
                <div class="notification-panel__empty">
                  <BellOffIcon :size="40" />
                  <p>{{ $t('notifications.empty') }}</p>
                </div>
              </template>

              <template v-else>
                <NotificationItem
                  v-for="notif in notifications"
                  :key="notif.id"
                  :notification="notif"
                  @click="handleNotificationClick"
                />

                <div v-if="isLoading" class="notification-panel__loading-more">
                  <div class="notification-panel__spinner notification-panel__spinner--small" />
                </div>
              </template>
            </div>

            <!-- Footer -->
            <div class="notification-panel__footer">
              <router-link
                to="/dashboard/notifications"
                class="notification-panel__view-all"
                @click="closePanel"
              >
                {{ $t('notifications.viewAll') }}
              </router-link>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { XIcon, BellOffIcon } from 'lucide-vue-next'
import { useRouter } from 'vue-router'
import NotificationItem from './NotificationItem.vue'
import { useNotifications } from '../composables/useNotifications'
import { openNotificationTarget } from '../utils/notificationRoute'
import type { Notification } from '../types/notifications.types'

const router = useRouter()
const {
  notifications,
  unreadCount,
  isLoading,
  isPanelOpen,
  closePanel,
  markRead,
  markAllRead,
  loadMore,
} = useNotifications()

function handleNotificationClick(notification: Notification) {
  if (!notification.isRead) {
    markRead(notification.id)
  }

  if (openNotificationTarget(notification, router)) {
    closePanel()
  }
}

async function handleMarkAllRead() {
  await markAllRead()
}

function handleScroll(event: Event) {
  const el = event.target as HTMLElement
  const threshold = 100
  if (el.scrollHeight - el.scrollTop - el.clientHeight < threshold) {
    loadMore()
  }
}
</script>

<style scoped>
.notification-panel-overlay {
  position: fixed;
  inset: 0;
  z-index: 50;
  background: rgba(0, 0, 0, 0.2);
}

.notification-panel {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 400px;
  max-width: 100vw;
  background: var(--color-background-elevated);
  box-shadow: var(--shadow-xl, -4px 0 20px rgba(0, 0, 0, 0.15));
  display: flex;
  flex-direction: column;
  z-index: 51;
}

.notification-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-md, 1rem) var(--spacing-lg, 1.25rem);
  border-bottom: 1px solid var(--color-border-light);
  flex-shrink: 0;
}

.notification-panel__title {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0;
  display: flex;
  align-items: center;
  gap: var(--spacing-sm, 0.5rem);
}

.notification-panel__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: 11px;
  background: var(--color-primary);
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
}

.notification-panel__actions {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm, 0.5rem);
}

.notification-panel__action {
  padding: 4px 12px;
  border: none;
  background: none;
  color: var(--color-primary);
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  border-radius: var(--radius-sm, 0.25rem);
  transition: background-color 0.15s;
}

.notification-panel__action:hover {
  background: var(--color-primary-bg, rgba(59, 130, 246, 0.08));
}

.notification-panel__close {
  width: 32px;
  height: 32px;
  border: none;
  background: none;
  color: var(--color-text-secondary);
  cursor: pointer;
  border-radius: var(--radius-sm, 0.25rem);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
}

.notification-panel__close:hover {
  background: var(--color-background-secondary);
  color: var(--color-text-primary);
}

.notification-panel__list {
  flex: 1;
  overflow-y: auto;
}

.notification-panel__loading,
.notification-panel__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-xxl, 3rem);
  color: var(--color-text-tertiary);
  gap: var(--spacing-md, 1rem);
}

.notification-panel__empty p {
  margin: 0;
  font-size: 0.875rem;
}

.notification-panel__spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--color-border-light);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.notification-panel__spinner--small {
  width: 20px;
  height: 20px;
  border-width: 2px;
}

.notification-panel__loading-more {
  display: flex;
  justify-content: center;
  padding: var(--spacing-md, 1rem);
}

.notification-panel__footer {
  border-top: 1px solid var(--color-border-light);
  padding: var(--spacing-sm, 0.75rem) var(--spacing-lg, 1.25rem);
  flex-shrink: 0;
}

.notification-panel__view-all {
  display: block;
  text-align: center;
  color: var(--color-primary);
  font-size: 0.875rem;
  font-weight: 500;
  text-decoration: none;
  padding: var(--spacing-xs, 0.5rem);
  border-radius: var(--radius-sm, 0.25rem);
  transition: background-color 0.15s;
}

.notification-panel__view-all:hover {
  background: var(--color-primary-bg, rgba(59, 130, 246, 0.08));
}

/* Animations */
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.panel-fade-enter-active,
.panel-fade-leave-active {
  transition: opacity 0.2s ease;
}

.panel-fade-enter-from,
.panel-fade-leave-to {
  opacity: 0;
}

.panel-slide-enter-active {
  transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.panel-slide-leave-active {
  transition: transform 0.2s ease-in;
}

.panel-slide-enter-from,
.panel-slide-leave-to {
  transform: translateX(100%);
}

@media (max-width: 480px) {
  .notification-panel {
    width: 100vw;
  }
}
</style>
